import React, { useCallback, useEffect, useState } from 'react';
import { authService } from './auth/authService';
import { resetAuthorizationService } from './services/authorizationService';
import Login from './components/Login';
import AppRouter from './components/AppRouter';
import './index.css';

const SESSION_EXPIRED_MESSAGE =
  'Your session expired. Please sign in again.';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applySessionEnded = useCallback((message = SESSION_EXPIRED_MESSAGE) => {
    resetAuthorizationService();
    setIsAuthenticated(false);
    setUser(null);
    setError(message);
  }, []);

  useEffect(() => {
    const unsubscribe = authService.onSessionEnded(() => {
      applySessionEnded();
    });
    return unsubscribe;
  }, [applySessionEnded]);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      await authService.initialize();
      const loggedIn = authService.isLoggedIn();
      setIsAuthenticated(loggedIn);

      if (loggedIn) {
        const userInfo = authService.getUserInfo();
        const photoUrl = await authService.getUserPhoto();
        setUser({ ...userInfo, photoUrl });
      }
    } catch (err) {
      if (authService.isSessionEndedError(err)) {
        applySessionEnded();
        return;
      }
      console.error('Auth initialization failed:', err);
      setError('Failed to initialize authentication. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      setError(null);
      await authService.login();
      setIsAuthenticated(true);
      const userInfo = authService.getUserInfo();
      const photoUrl = await authService.getUserPhoto();
      setUser({ ...userInfo, photoUrl });
    } catch (err) {
      if (authService.isSessionEndedError(err)) {
        applySessionEnded();
        return;
      }
      console.error('Login failed:', err);
      setError('Login failed. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      setError(null);
      resetAuthorizationService();
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
    } catch (err) {
      console.error('Logout failed:', err);
      setError('Logout failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading">Loading Wolfwalker...</div>
      </div>
    );
  }

  return (
    <div className="App">
      {error && (
        <div className="error-banner">
          <div className="error-content">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="error-close">×</button>
          </div>
        </div>
      )}

      {isAuthenticated ? (
        <AppRouter user={user} onLogout={handleLogout} />
      ) : (
        <Login onLogin={handleLogin} />
      )}
    </div>
  );
}

export default App;
